// Vendor Purchase History — Add/Edit form.
// Mirrors src/Components/inventory/ProcurementFormSheet.tsx's Sheet structure
// and src/Components/inventory/AddProductSheet.tsx's Combobox-driven vendor
// picker. Same form serves add and edit — a vendor can have many purchase
// rows (it's a log, not one row per vendor), so edit just pre-populates all
// three fields onto a fresh entry.
import * as React from "react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "ui/sheet";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Combobox } from "ui/combobox";
import { FieldRow } from "../../shared/form-section";
import AppConstant from "../../Globals/AppConstants";

export interface VendorPurchaseFormSheetProps {
    open: boolean;
    onClose: () => void;
    vendorOptions: { value: string; label: string }[];
    formData: any;
    onChange: (patch: any) => void;
    /** Fires only when validation passes; runs the screen's createOrUpdateVendorPurchaseApi. */
    onSubmit: () => void;
}

type Errors = { vendorId?: string; purchaseDate?: string; gpayAmount?: string };

const todayStr = new Date().toISOString().slice(0, 10);

// Whole-rupee amounts only — displayed comma-grouped (e.g. 1,00,000), no
// decimals. Digits are the source of truth in formData; commas are display-only.
const toDigits = (value: string) => value.replace(/[^0-9]/g, "");
const formatAmount = (value: any) => {
    const digits = toDigits(String(value ?? ""));
    return digits ? Number(digits).toLocaleString("en-IN") : "";
};

export function VendorPurchaseFormSheet({
    open,
    onClose,
    vendorOptions,
    formData,
    onChange,
    onSubmit,
}: VendorPurchaseFormSheetProps) {
    const [errors, setErrors] = React.useState<Errors>({});

    const totalPurchaseAmount =
        (Number(formData?.gpayAmount) || 0) + (Number(formData?.cashAmount) || 0) + (Number(formData?.shippingCharges) || 0);

    const validate = (): boolean => {
        const next: Errors = {};
        if (!formData?.vendorId) next.vendorId = "Vendor is required";
        if (!formData?.purchaseDate) next.purchaseDate = "Purchase Date is required";
        if (totalPurchaseAmount <= 0) next.gpayAmount = "Enter a valid GPay and/or Cash amount";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) onSubmit();
    };

    const close = () => {
        setErrors({});
        onClose();
    };

    return (
        <Sheet open={open} onOpenChange={(o) => { if (!o) close(); }}>
            <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
                <SheetHeader className="border-b px-5 py-4">
                    <SheetTitle>{formData?.vendorPurchaseId ? "Edit Purchase" : "Add Purchase"}</SheetTitle>
                </SheetHeader>
                <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
                    {formData?.vendorPurchaseId && formData?.invoiceId && (
                        <FieldRow label="Bill No">
                            <p className="text-sm font-medium text-foreground">{formData.invoiceId}</p>
                        </FieldRow>
                    )}

                    <FieldRow label="Vendor" htmlFor="vendor" required error={errors.vendorId}>
                        <Combobox
                            id="vendor"
                            options={vendorOptions}
                            value={formData?.vendorId ? String(formData.vendorId) : undefined}
                            onValueChange={(v) => { onChange({ vendorId: v }); setErrors((e) => ({ ...e, vendorId: undefined })); }}
                            placeholder="Select vendor"
                            searchPlaceholder="Search vendor..."
                        />
                    </FieldRow>

                    <FieldRow label="Purchase Date" required error={errors.purchaseDate}>
                        <Input
                            type="date"
                            max={todayStr}
                            value={formData?.purchaseDate ?? ""}
                            onChange={(e) => { onChange({ purchaseDate: e.target.value }); setErrors((er) => ({ ...er, purchaseDate: undefined })); }}
                        />
                    </FieldRow>

                    <div className="grid grid-cols-3 gap-3">
                        <FieldRow label="GPay" required error={errors.gpayAmount}>
                            <Input
                                type="text"
                                inputMode="numeric"
                                value={formatAmount(formData?.gpayAmount)}
                                onChange={(e) => { onChange({ gpayAmount: toDigits(e.target.value) }); setErrors((er) => ({ ...er, gpayAmount: undefined })); }}
                            />
                        </FieldRow>

                        <FieldRow label="Cash">
                            <Input
                                type="text"
                                inputMode="numeric"
                                value={formatAmount(formData?.cashAmount)}
                                onChange={(e) => { onChange({ cashAmount: toDigits(e.target.value) }); setErrors((er) => ({ ...er, gpayAmount: undefined })); }}
                            />
                        </FieldRow>

                        <FieldRow label="Shipping Charges">
                            <Input
                                type="text"
                                inputMode="numeric"
                                value={formatAmount(formData?.shippingCharges)}
                                onChange={(e) => onChange({ shippingCharges: toDigits(e.target.value) })}
                            />
                        </FieldRow>
                    </div>

                    <FieldRow label="Total Purchase Amount">
                        <Input
                            type="text"
                            readOnly
                            disabled
                            className="bg-muted font-medium"
                            value={formatAmount(String(totalPurchaseAmount))}
                        />
                    </FieldRow>

                    <div className="mt-auto flex justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={close}>{AppConstant?.cancel}</Button>
                        <Button type="submit">{AppConstant?.save}</Button>
                    </div>
                </form>
            </SheetContent>
        </Sheet>
    );
}

export default VendorPurchaseFormSheet;
